import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Form, Input, message, Select, Space, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import { getBenefitSendRecordNamesApi, getBenefitSendRecordPageApi } from '@/api/member/member';
import { state } from '@/store';

const { RangePicker } = DatePicker;

type BenefitsNameOption = {
  label: string;
  value: string;
};

const allBenefitsNameOption: BenefitsNameOption = { label: '全部', value: '' };

const benefitsSendStatusMap: Record<string, { label: string; color: string }> = {
  WAIT: { label: '待发放', color: 'processing' },
  SUCCESS: { label: '已发放', color: 'success' },
  FAIL: { label: '发放失败', color: 'error' },
};

const MembershipBenefitsSendRecord: React.FC = () => {
  const { currentShopId } = useSnapshot(state);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [benefitsNameOptions, setBenefitsNameOptions] = useState<BenefitsNameOption[]>([allBenefitsNameOption]);
  const [benefitsNameLoading, setBenefitsNameLoading] = useState(false);

  const columns = useMemo(() => {
    const base = [
      { title: '权益类型', dataIndex: 'benefitsName' },
      {
        title: '发放明细',
        dataIndex: 'benefitsSendValue',
        render: (_: string, record: any) => {
          const value = record?.benefitsSendValue;
          if (!value) return '—';
          return record?.benefitsSendType === 'THIRD_PLAT' ? String(value) : `${value}积分`;
        },
      },
      {
        title: '会员平台ID',
        dataIndex: 'platformId',
        render: (v: string) => (
          <span style={{ maxWidth: 220, display: 'inline-block', wordBreak: 'break-all', whiteSpace: 'normal' }}>
            {v || '—'}
          </span>
        ),
      },
      { title: 'OneID', dataIndex: 'oneId' },
      { title: '手机号', dataIndex: 'mobile', render: (v: string) => v || '—' },
      {
        title: '会员等级',
        dataIndex: 'memberLeve',
        render: (v: string) => (v ? `${v}星会员` : '—'),
      },
      { title: '发放时间', dataIndex: 'sendTime' },
      {
        title: '发放状态',
        dataIndex: 'benefitsSendStatus',
        render: (v: string) => {
          if (!v) return '—';
          const status = benefitsSendStatusMap[v];
          if (!status) return v;
          return <Tag color={status.color}>{status.label}</Tag>;
        },
      },
    ];
    return base.map(col => ({
      ...col,
      title: <span style={{ whiteSpace: 'nowrap' }}>{col.title}</span>,
    }));
  }, []);

  const fetchList = async (override?: { pageNum?: number; pageSize?: number }) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const values = form.getFieldsValue();
    const params: any = {
      benefitsName: values.benefitsName ?? '',
      mobile: values.mobile || '',
      platformId: values.platformId || '',
      oneId: values.oneId || '',
      startSendTime: values.sendTime?.length ? dayjs(values.sendTime[0]).format('YYYY-MM-DD HH:mm:ss') : '',
      endSendTime: values.sendTime?.length ? dayjs(values.sendTime[1]).format('YYYY-MM-DD HH:mm:ss') : '',
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    setLoading(true);
    try {
      const res = await getBenefitSendRecordPageApi(currentShopId, params);
      if (res?.code === 200) {
        setData(res.rows || res.data?.rows || []);
        setTotal(res.total || res.data?.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      } else {
        message.error(res?.msg || '获取列表失败');
      }
    } catch (error) {
      message.error('获取列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchBenefitsNameOptions = async () => {
    if (!currentShopId) {
      setBenefitsNameOptions([allBenefitsNameOption]);
      return;
    }
    setBenefitsNameLoading(true);
    try {
      const res = await getBenefitSendRecordNamesApi(currentShopId);
      if (res?.code === 200) {
        const names = Array.isArray(res.data) ? res.data : [];
        setBenefitsNameOptions([allBenefitsNameOption, ...names.map((name: string) => ({ label: name, value: name }))]);
      } else {
        setBenefitsNameOptions([allBenefitsNameOption]);
        message.error(res?.msg || '获取权益类型失败');
      }
    } catch (error) {
      setBenefitsNameOptions([allBenefitsNameOption]);
      message.error('获取权益类型失败');
    } finally {
      setBenefitsNameLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldValue('benefitsName', '');
    fetchBenefitsNameOptions();
    fetchList({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  const handleReset = () => {
    form.resetFields();
    fetchList({ pageNum: 1 });
  };

  return (
    <div style={{ padding: 24 }}>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12, columnGap: 12 }}>
        <Form.Item
          label="权益类型"
          name="benefitsName"
          initialValue="">
          <Select
            loading={benefitsNameLoading}
            options={benefitsNameOptions}
            style={{ width: 140 }}
          />
        </Form.Item>
        <Form.Item
          label="手机号"
          name="mobile">
          <Input
            placeholder="请输入"
            allowClear
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item
          label="会员平台ID"
          name="platformId">
          <Input
            placeholder="请输入"
            allowClear
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item
          label="oneId"
          name="oneId">
          <Input
            placeholder="请输入"
            allowClear
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item
          label="发放时间"
          name="sendTime">
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
          />
        </Form.Item>
        <Form.Item>
          <Space size={12}>
            <Button
              type="primary"
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey={(record: any, index) => record.id || `${record.oneId}-${record.sendTime}-${index}`}
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (pn, ps) => fetchList({ pageNum: pn, pageSize: ps }),
        }}
      />
    </div>
  );
};

export default MembershipBenefitsSendRecord;
